import React from 'react'
import styled, { css } from 'styled-components'
import { toCssPrefix, useResolution, breakpoints } from '@ecommerce/shared'
import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperCore, { Controller } from 'swiper'
import SectionTitle from '../SectionTitle'
import PromoCardXL from '../../PromoList/PromoCardXL'
import useQuery from '../../../hooks/useQuery'
import promoSlideQuery, { QueryPromoSlideResult } from '../../../graphql/promoSlideQuery'
import slugFallback from '../../../utils/slugFallback'

SwiperCore.use([Controller])

const { cssPrefix, toPrefix } = toCssPrefix('PromosGrid__')

type Props = {
  listId: string
}

const singleItemRatio = 0.61

const gridStyle = css`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`

const singleStyle = css`
  display: flex;
  justify-content: center;
`

const StyledSwiper = styled(Swiper)`
  .swiper {
    &-wrapper {
      padding: 14px 0 14px 0;
    }
    &-wrapper div:last-child {
      padding-right: 10px;
    }
    &-slide {
      width: auto;
      height: fit-content;
    }
    &-button {
      &-next,
      &-prev {
        display: flex;
        color: ${({ theme }) => theme.colors.black};
        height: 40px;
        width: 40px;
        transition: 200ms;
        &::after {
          font-weight: bolder;
          font-size: 12px;
        }
        right: 10px;
      }
      &-next {
        right: 5px;
      }
      &-prev {
        left: 5px;
      }
    }
  }
`

const Wrapper = styled.div<{ isSingle: boolean }>`
  color: ${({ theme }) => theme.colors.bodyText};
  margin: 0 auto;
  max-width: 1400px;
  padding: 20px 0 0 20px;

  .${cssPrefix} {
    &slider {
      padding: 14px 0;
    }

    &grid {
      ${({ isSingle }) => (isSingle ? singleStyle : '')};
      padding: 0 0 14px 0;
    }
  }

  @media screen and (${breakpoints.desktop.min}) {
    padding: 20px 82px 0 82px;
    .${cssPrefix} {
      &grid {
        ${({ isSingle }) => (isSingle ? singleStyle : gridStyle)}
        gap: 16px;
        padding: 0 0 14px 0;
      }

      &item {
        max-width: 700px;
        ${({ isSingle }) =>
          isSingle
            ? css`
                width: 50%;
                min-width: 622px;
                height: 318px;
                min-height: calc(622px * ${singleItemRatio});
              `
            : css`
                &:nth-child(odd) {
                  margin-left: auto;
                }

                &:nth-child(even) {
                  margin-right: auto;
                }
              `}
      }
    }
  }
`

const PromoGrid = ({ listId }: Props) => {
  const { data, loading, error } = useQuery<QueryPromoSlideResult>(promoSlideQuery, { variables: { listId } })

  const orList = data?.orList
  const promos = orList?.itemsCollection.items

  const { isDesktop } = useResolution()

  if (error !== undefined || (!promos?.length && !loading)) return null

  const renderItems = () =>
    promos?.map(({ sys: { id }, imageDesktop, imageMobile, imageTablet, actionSlug, actionSlugLong }) => {
      const images = {
        desktop: imageDesktop.url,
        mobile: imageMobile.url,
        tablet: imageTablet.url,
      }

      return (
        <PromoCardXL
          images={images}
          actionSlug={slugFallback(actionSlug) ?? slugFallback(actionSlugLong)}
          className={toPrefix('item')}
          key={id}
        />
      )
    })

  const isSingle = promos?.length === 1

  return (
    <Wrapper className={cssPrefix} isSingle={isSingle}>
      {orList?.title && <SectionTitle text={orList?.title} color={orList?.titleColor} {...orList} />}
      <div className={toPrefix('grid')}>
        {isDesktop || isSingle ? (
          renderItems()
        ) : (
          <StyledSwiper spaceBetween={40} slidesPerView="auto" scrollbar={false}>
            {promos?.map((item) => (
              <SwiperSlide>
                <PromoCardXL
                  images={{
                    desktop: item.imageDesktop.url,
                    mobile: item.imageMobile.url,
                    tablet: item.imageTablet.url,
                  }}
                  actionSlug={slugFallback(item.actionSlug) ?? slugFallback(item.actionSlugLong)}
                  className={toPrefix('item')}
                  key={item.sys.id}
                />
              </SwiperSlide>
            ))}
          </StyledSwiper>
        )}
      </div>
    </Wrapper>
  )
}

export default PromoGrid
